import React from 'react';
import styled from 'styled-components';
import { AddressAutocomplete, AddressData } from '../../../../common/AddressAutocomplete';
import { Input } from '../../../../common/Input';
import { ContactField } from '../../styles';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { ExtendedMarkerData } from '../../../../../types/marker';
import { v4 as uuidv4 } from 'uuid';

interface ContactAddressFieldsProps {
  formData: ExtendedMarkerData;
  setFormData: React.Dispatch<React.SetStateAction<ExtendedMarkerData>>;
  errors?: {
    contact_address?: string;
    postalCode?: string;
    city?: string;
  };
  isReadOnly?: boolean;
  contactIndex?: number; // Index du contact actif
}

const AddressRow = styled.div`
  display: flex;
  gap: 6px;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const AddressField = styled.div`
  flex: 1;
  min-width: 0;
  
  @media (max-width: 768px) {
    input {
      font-size: 0.75rem !important;
      height: 26px !important;
      padding: 0 12px !important;
      padding-left: 24px !important;
    }
  }
`;

const SmallField = styled.div`
  flex: 0 0 120px;
  
  @media (max-width: 768px) {
    flex: 1;
  }
`;

const MediumField = styled.div`
  flex: 1 1 auto;
  max-width: 180px;
  
  @media (max-width: 768px) {
    flex: 1;
    max-width: none;
  }
`;

export const ContactAddressFields: React.FC<ContactAddressFieldsProps> = ({
  formData,
  setFormData,
  errors,
  isReadOnly = false,
  contactIndex = 0
}) => {
  const currentContact = formData.contacts?.[contactIndex];
  // Pour le 1er contact, utiliser contact_address comme fallback
  // Pour les autres contacts, ne pas utiliser de fallback (champs vides)
  const currentAddress = currentContact?.address || (contactIndex === 0 ? formData.contact_address || '' : '');
  const currentPostalCode = currentContact?.postalCode || '';
  const currentCity = currentContact?.city || '';

  const handleAddressChange = (addressData: AddressData) => {
    const updatedContacts = [...(formData.contacts || [])];
    
    if (updatedContacts.length === 0 || !updatedContacts[contactIndex]) {
      // Créer un nouveau contact si aucun n'existe
      const newContact = {
        id: uuidv4(),
        name: formData.name || '',
        firstName: formData.firstName || '',
        type: 'owner' as const,
        address: addressData.address,
        postalCode: addressData.postalCode,
        city: addressData.city,
        phone: formData.phone || '',
        email: formData.email || '',
        designation: 'contact' as const
      };
      updatedContacts.push(newContact);
    } else {
      // Mettre à jour le contact actif
      updatedContacts[contactIndex] = {
        ...updatedContacts[contactIndex],
        address: addressData.address,
        postalCode: addressData.postalCode,
        city: addressData.city
      };
    }
    
    // Synchroniser avec contact_address seulement pour le 1er contact
    const updates: any = { contacts: updatedContacts };
    if (contactIndex === 0) {
      updates.contact_address = addressData.address;
    }
    
    setFormData((prev: ExtendedMarkerData) => ({
      ...prev,
      ...updates
    }));
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPostalCode = e.target.value;
    const updatedContacts = [...(formData.contacts || [])];
    
    if (updatedContacts.length === 0 || !updatedContacts[contactIndex]) {
      const newContact = {
        id: uuidv4(),
        name: formData.name || '',
        firstName: formData.firstName || '',
        type: 'owner' as const,
        address: formData.contact_address || '',
        postalCode: newPostalCode,
        city: currentCity,
        phone: formData.phone || '',
        email: formData.email || '',
        designation: 'contact' as const
      };
      updatedContacts.push(newContact);
    } else {
      updatedContacts[contactIndex] = {
        ...updatedContacts[contactIndex],
        postalCode: newPostalCode
      };
    }
    
    setFormData((prev: ExtendedMarkerData) => ({
      ...prev,
      contacts: updatedContacts
    }));
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCity = e.target.value;
    const updatedContacts = [...(formData.contacts || [])];
    
    if (updatedContacts.length === 0 || !updatedContacts[contactIndex]) {
      const newContact = {
        id: uuidv4(),
        name: formData.name || '',
        firstName: formData.firstName || '',
        type: 'owner' as const,
        address: formData.contact_address || '',
        postalCode: currentPostalCode,
        city: newCity,
        phone: formData.phone || '',
        email: formData.email || '',
        designation: 'contact' as const
      };
      updatedContacts.push(newContact);
    } else {
      updatedContacts[contactIndex] = {
        ...updatedContacts[contactIndex],
        city: newCity
      };
    }
    
    setFormData((prev: ExtendedMarkerData) => ({
      ...prev,
      contacts: updatedContacts
    }));
  };

  return (
    <>
      {/* Ligne d'adresse avec autocomplétion */}
      <AddressRow>
        <AddressField>
          <AddressAutocomplete
            value={currentAddress}
            onChange={handleAddressChange}
            placeholder="Rechercher une adresse..."
            error={errors?.contact_address}
            disabled={isReadOnly}
          />
        </AddressField>
      </AddressRow>
      
      {/* Ligne code postal et ville */}
      <AddressRow>
        <SmallField>
          <ContactField>
            <FaMapMarkerAlt className="field-icon" size={14} />
            <Input
              type="text"
              placeholder="Code postal"
              value={currentPostalCode}
              onChange={handlePostalCodeChange}
              error={errors?.postalCode}
              disabled={isReadOnly}
            />
          </ContactField>
        </SmallField>
        <MediumField>
          <ContactField>
            <FaMapMarkerAlt className="field-icon" size={14} />
            <Input
              type="text"
              placeholder="Ville"
              value={currentCity}
              onChange={handleCityChange}
              error={errors?.city}
              disabled={isReadOnly}
            />
          </ContactField>
        </MediumField>
      </AddressRow>
    </>
  );
};
